/**
 * PHASE 8: Mobile Day Detail Cards
 * PHASE 3: Converted to bottom sheet pattern for focused day view
 * Shows reservations/events for the selected day as compact cards
 */

import React, { useState } from 'react';
import { formatDateOnlyET } from '../../utils/dateTimeET.js';
import { getSourceLabel, getSourceColorHex } from '../../utils/sourceColors.js';
import BottomSheet from './BottomSheet.jsx';
import FinancialDetailsModal from './FinancialDetailsModal.jsx';
import { SkeletonCard, SkeletonText } from '../shared/SkeletonLoader.jsx';

export default function MobileDayDetails({ 
  selectedDate, 
  bookings = [], 
  onBookingClick,
  isMobile = false,
  onCollapseDetails,
  isLoading = false // B4: Loading state for skeleton
}) {
  const [selectedFinancialBooking, setSelectedFinancialBooking] = useState(null);
  if (!isMobile || !selectedDate) {
    return null;
  }

  // Filter bookings for the selected day
  const selectedDateStr = selectedDate instanceof Date 
    ? selectedDate.toISOString().split('T')[0]
    : new Date(selectedDate).toISOString().split('T')[0];

  const dayBookings = bookings.filter(booking => {
    if (!booking.check_in_date || !booking.check_out_date) return false;
    const checkIn = new Date(booking.check_in_date).toISOString().split('T')[0];
    const checkOut = new Date(booking.check_out_date).toISOString().split('T')[0];
    return selectedDateStr >= checkIn && selectedDateStr < checkOut;
  });

  const formattedDate = formatDateOnlyET(selectedDateStr, { 
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // B1: Compute peek summary for collapsed state
  const computePeekSummary = () => {
    if (dayBookings.length === 0) return null;
    const bookingCount = dayBookings.length;
    const checkInCount = dayBookings.filter(b => {
      const checkIn = b.check_in_date ? new Date(b.check_in_date).toISOString().split('T')[0] : null;
      return checkIn === selectedDateStr;
    }).length;
    const checkOutCount = dayBookings.filter(b => {
      const checkOut = b.check_out_date ? new Date(b.check_out_date).toISOString().split('T')[0] : null;
      return checkOut === selectedDateStr;
    }).length;
    
    // Compute total owner payout if available
    let totalPayout = null;
    try {
      const payouts = dayBookings
        .map(b => b.canonical_financial_summary?.owner_payout?.owner_payout_total)
        .filter(v => v != null && !isNaN(v));
      if (payouts.length > 0) {
        totalPayout = payouts.reduce((sum, v) => sum + v, 0);
      }
    } catch (e) {
      // Ignore financial calculation errors
    }
    
    const parts = [];
    parts.push(`${bookingCount} ${bookingCount === 1 ? 'booking' : 'bookings'}`);
    if (checkInCount > 0) parts.push(`${checkInCount} check-in`);
    if (checkOutCount > 0) parts.push(`${checkOutCount} check-out`);
    if (totalPayout != null) {
      parts.push(`$${totalPayout.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} owner payout`);
    } else {
      parts.push('Financials unavailable');
    }
    
    return parts.join(' • ');
  };

  const peekSummary = computePeekSummary();

  // PHASE 3: Use BottomSheet for focused day view
  return (
    <>
    <BottomSheet
      isOpen={true}
      onClose={onCollapseDetails || (() => {})}
      title={formattedDate}
      disableDrag={!!selectedFinancialBooking} // BEYOND WORLD-CLASS: Disable drag when financial modal open
      peekSummary={peekSummary} // B1: Peek summary row
    >
      <div style={styles.container} data-day-details-sheet="true">

      {/* B4: Skeleton loading state */}
      {isLoading ? (
        <div style={styles.bookingsList}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={styles.bookingCardSkeleton}>
              <SkeletonText lines={2} width="80%" />
              <SkeletonText lines={1} width="60%" />
            </div>
          ))}
        </div>
      ) : dayBookings.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No bookings for this day</p>
        </div>
      ) : (
        <div style={styles.bookingsList}>
          {dayBookings.map((booking, index) => {
            const channelLabel = getSourceLabel(booking.channel_code || booking.source || '');
            const channelColor = getSourceColorHex(booking.channel_code || booking.source || '');
            const guestName = booking.guest_name || booking.guestName || 'Guest';
            const checkIn = booking.check_in_date ? formatDateOnlyET(booking.check_in_date, { monthDay: true }) : null;
            const checkOut = booking.check_out_date ? formatDateOnlyET(booking.check_out_date, { monthDay: true }) : null;
            const isCheckIn = checkIn && selectedDateStr === new Date(booking.check_in_date).toISOString().split('T')[0];
            const isCheckOut = checkOut && selectedDateStr === new Date(booking.check_out_date).toISOString().split('T')[0];

            return (
              <div
                key={booking.reservation_id || booking.id || index}
                data-day-details-event="true"
                data-reservation-id={booking.reservation_id || booking.id}
                onClick={() => onBookingClick && onBookingClick(booking)}
                style={styles.bookingCard}
                role="button"
                tabIndex={0}
                aria-label={`${guestName} - ${channelLabel}${isCheckIn ? ' - Check-in' : ''}${isCheckOut ? ' - Check-out' : ''}`}
                // PHASE 9D: Touch feedback - active state
                onTouchStart={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover, #f3f4f6)';
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && onBookingClick) {
                    e.preventDefault();
                    onBookingClick(booking);
                  }
                }}
              >
                {/* WORLD-CLASS: Clear hierarchy - guest name primary, dates secondary, channel tertiary */}
                <div style={styles.bookingHeader}>
                  <div style={styles.bookingLeft}>
                    <div style={styles.guestName}>{guestName}</div>
                    {(checkIn || checkOut) && (
                      <div style={styles.bookingDates}>
                        {checkIn && <span>{checkIn}</span>}
                        {checkIn && checkOut && <span style={styles.dateSeparator}>→</span>}
                        {checkOut && <span>{checkOut}</span>}
                      </div>
                    )}
                  </div>
                  <div style={styles.bookingRight}>
                    {(isCheckIn || isCheckOut) && (
                      <div style={styles.eventTag}>
                        {isCheckIn && <span style={styles.checkInTag}>Check-in</span>}
                        {isCheckOut && <span style={styles.checkOutTag}>Check-out</span>}
                      </div>
                    )}
                    {/* WORLD-CLASS: Channel badge subtle and tertiary */}
                    <div style={styles.channelBadge}>
                      <span 
                        style={{
                          ...styles.channelDot,
                          backgroundColor: channelColor,
                        }}
                      />
                      <span style={styles.channelLabel}>{channelLabel}</span>
                    </div>
                  </div>
                </div>
                {/* BEYOND WORLD-CLASS: View financials button */}
                {booking.canonical_financial_summary && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFinancialBooking(booking);
                    }}
                    style={styles.viewFinancialsButton}
                    aria-label={`View financial details for ${guestName}`}
                    data-view-financials="true"
                  >
                    View Financials
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </BottomSheet>
    {selectedFinancialBooking && (
      <FinancialDetailsModal
        isOpen={!!selectedFinancialBooking}
        onClose={() => setSelectedFinancialBooking(null)}
        booking={selectedFinancialBooking}
        isMobile={isMobile}
        onRetryFetch={onBookingClick ? async (reservationId) => {
          // PREMIUM: Retry fetch by re-opening booking detail modal which will fetch fresh data
          // Find the booking in the list and trigger onBookingClick
          const bookingToRetry = bookings.find(b => 
            (b.reservation_id && String(b.reservation_id) === String(reservationId)) ||
            (b.id && String(b.id) === String(reservationId))
          );
          if (bookingToRetry && onBookingClick) {
            onBookingClick(bookingToRetry);
            // Close financial modal and let booking detail modal handle the fetch
            setSelectedFinancialBooking(null);
          }
        } : undefined}
      />
    )}
  </>
  );
}

const styles = {
  // PHASE 3: Container for bottom sheet content (no padding needed - BottomSheet handles it)
  container: {
    // BottomSheet already provides padding via mobileContent style
    // Remove inline padding/margin since we're in a bottom sheet now
  },
  emptyState: {
    padding: 'var(--spacing-6) var(--spacing-4)',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
    margin: 0,
  },
  // B4: Skeleton card styles
  bookingCardSkeleton: {
    padding: 'var(--spacing-4)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border-light)',
    marginBottom: 'var(--spacing-3)',
    minHeight: '80px',
  },
  bookingsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)', // WORLD-CLASS V2: Tighter gap between cards
  },
  // WORLD-CLASS V2: Booking card - Compact list-like density
  bookingCard: {
    padding: 'var(--spacing-3) var(--spacing-4)', // WORLD-CLASS V2: Reduced vertical padding
    backgroundColor: 'var(--color-bg-secondary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'background-color 80ms ease-out, transform 80ms ease-out',
    minHeight: '64px', // WORLD-CLASS V2: Slightly reduced (still comfortable)
    display: 'flex',
    flexDirection: 'column',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    marginBottom: 'var(--spacing-1)', // WORLD-CLASS V2: Tighter spacing between cards
  },
  bookingHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--spacing-2)', // WORLD-CLASS V2: Tighter gap
  },
  bookingLeft: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  bookingRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 'var(--spacing-1)',
    flexShrink: 0,
  },
  guestName: {
    fontSize: 'var(--font-size-base)', // WORLD-CLASS V2: Base size (was lg) for compactness
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: '2px', // WORLD-CLASS V2: Tighter spacing
    lineHeight: 1.3,
  },
  channelBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
    marginTop: 'var(--spacing-1)', // WORLD-CLASS: Space from event tag
  },
  channelDot: {
    width: '6px', // WORLD-CLASS: Smaller, more subtle
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
    opacity: 0.8, // WORLD-CLASS: Slightly muted
  },
  channelLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)', // WORLD-CLASS: Tertiary color for subtlety
    fontWeight: 'var(--font-weight-normal)', // WORLD-CLASS: Normal weight, not medium
  },
  eventTag: {
    display: 'flex',
    gap: 'var(--spacing-1)',
    flexShrink: 0,
  },
  checkInTag: {
    padding: 'var(--spacing-1) var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: '#ffffff',
    backgroundColor: 'var(--color-status-success-base, #10b981)',
    borderRadius: 'var(--radius-sm)',
  },
  checkOutTag: {
    padding: 'var(--spacing-1) var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: '#ffffff',
    backgroundColor: 'var(--color-status-warning-base, #f59e0b)',
    borderRadius: 'var(--radius-sm)',
  },
  bookingDates: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
    fontSize: 'var(--font-size-xs)', // WORLD-CLASS V2: Smaller for compactness
    color: 'var(--color-text-secondary)',
    fontWeight: 'var(--font-weight-normal)',
    marginTop: '0px', // WORLD-CLASS V2: No margin (tight spacing)
  },
  dateSeparator: {
    color: 'var(--color-text-tertiary)',
  },
  viewFinancialsButton: {
    marginTop: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-3)',
    backgroundColor: 'var(--color-brand-primary, #2563eb)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: 'pointer',
    width: '100%',
    minHeight: '44px',
    transition: 'background-color 120ms ease-out',
    touchAction: 'manipulation',
  },
};
